import { useEffect, useMemo, useState } from 'react';
import { Table, Modal, Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import './MercurXContractTable.scss';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { abiRequestAction } from '../../store/abi/abiActions';
import MercurXIcons from '../MercurXIcons/MercurXIcons';
import { mainColors } from '../../helpers/colors';
import useMercurxPagination from '../../hooks/useMercurxPagination';
import MercurXTablePagination from '../MercurXTablePagination/MercurXTablePagination';
import MercurXSortableHeader from '../MercurXSortableHeader/MercurXSortableHeader';
import {
  createContractActionRequest,
  updateContractActionRequest,
} from '../../store/abi/abiActions';
import Swal from 'sweetalert2';

const CONTRACTS_SORT_TYPES = {
  ASCENDING: 0,
  DESCENDING: 1,
};

function MercurXContractTable(props) {
  const { abiHistory, abiHistoryRequest, filterInput,
    createContractRequest,
    updateContractRequest, } = props;
  const history = useHistory();
  const [contractsSortData, setContractsSortData] = useState({
    sortKey: 0,
    sortType: CONTRACTS_SORT_TYPES.ASCENDING,
  });
  const [selectedAbi, setSelectedAbi] = useState(null);
  const [selectedCode, setSelectedCode] = useState(null);
  const [selectedByteCode, setSelectedByteCode] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [uploadContractAddress, setUploadContractAddress] = useState('');
  const [initialUploadContractAddress, setInitialUploadContractAddress] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCreateAbi, setSelectedCreateAbi] = useState(null);
  const [selectedCreateCode, setSelectedCreateCode] = useState(null);
  const [selectedCreateByteCode, setSelectedCreateByteCode] = useState(null);
  const [createContractAddress, setCreateContractAddress] = useState('');
 
  useEffect(() => {
    abiHistoryRequest();
  }, [abiHistoryRequest]);

  const sourceData = useMemo(() => {
    if (!abiHistory) {
      return [];
    }

    let nextSourceData = Object.keys(abiHistory).map((key) => ({
      name: key,
      data: abiHistory[key],
    }));

    if (filterInput.trim() !== '') {
      nextSourceData = nextSourceData.filter((item) =>
        item.name.toLowerCase().includes(filterInput.toLowerCase())
      );
    }

    return [...nextSourceData].sort((itemA, itemB) => {
      const compareResult = itemA.name.toLowerCase().localeCompare(itemB.name.toLowerCase());

      return contractsSortData.sortType === CONTRACTS_SORT_TYPES.ASCENDING
        ? compareResult
        : -compareResult;
    });
  }, [abiHistory, contractsSortData.sortType, filterInput]);

  const {
    currentPage,
    totalPages,
    visiblePages,
    pagedData: renderedItems,
    rowOffset,
    isFirstPage,
    isLastPage,
    goToPage,
    goToFirstPage,
    goToPrevPage,
    goToNextPage,
    goToLastPage,
  } = useMercurxPagination(sourceData, 5, 5);

  function scrollToContainer() {
    const container = document.getElementById('data-container');
    if (container) {
      container.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const isValidEthAddress = (value) => /^0x[a-fA-F0-9]{40}$/.test((value || '').trim());

  const handlePageChange = (page) => {
    goToPage(page);
    scrollToContainer();
  };

  const handleFirstPage = () => {
    goToFirstPage();
    scrollToContainer();
  };

  const handlePrevPage = () => {
    goToPrevPage();
    scrollToContainer();
  };

  const handleNextPage = () => {
    goToNextPage();
    scrollToContainer();
  };

  const handleLastPage = () => {
    goToLastPage();
    scrollToContainer();
  };
  const handleNavigate = (itemName, item) => {
    history.push('/mercurx-contract', { itemName, item });
  };
  const handleNavigateEdit = (itemName, item) => {
    history.push('/contract-edit', { itemName, item });
  };

  const handleSort = () => {
    setContractsSortData((currentSortData) => ({
      ...currentSortData,
      sortType: currentSortData.sortType === CONTRACTS_SORT_TYPES.ASCENDING
        ? CONTRACTS_SORT_TYPES.DESCENDING
        : CONTRACTS_SORT_TYPES.ASCENDING,
    }));
  };

  const handleOpenModal = (item) => {
    const existingContractAddress = item?.data?.contract_address || '';
    setSelectedItemId(item?.data?.id);
    setUploadContractAddress(existingContractAddress);
    setInitialUploadContractAddress(existingContractAddress);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedAbi(null);
    setSelectedCode(null);
    setSelectedByteCode(null);
    setSelectedItemId(null);
    setUploadContractAddress('');
    setInitialUploadContractAddress('');
    setShowModal(false);
  };
  const handleAbiChange = (event) => setSelectedAbi(event.target.files[0]);
  const handleCodeChange = (event) => setSelectedCode(event.target.files[0]);
  const handleByteCodeChange = (event) => setSelectedByteCode(event.target.files[0]);

  const handleUpload = () => {
    const trimmedContractAddress = uploadContractAddress.trim();
    const hasSelectedFile = selectedAbi || selectedCode || selectedByteCode;
    const isAddressChanged = trimmedContractAddress !== initialUploadContractAddress;

    if (!isValidEthAddress(trimmedContractAddress)) {
      Swal.fire({
        icon: 'warning',
        iconColor: mainColors.warning,
        text: 'Please enter a valid contract address.',
        showCancelButton: true,
      });
      return;
    }

    if (!hasSelectedFile && !isAddressChanged) {
      Swal.fire({
        icon: 'warning',
        iconColor: mainColors.warning,
        text: 'Please update the contract address or select at least one file.',
        showCancelButton: true,
      });
      return;
    }
  
    const formData = new FormData();
  
    if (selectedAbi) formData.append("abi", selectedAbi);
    if (selectedCode) formData.append("code", selectedCode);
    if (selectedByteCode) formData.append("byte_code", selectedByteCode);
    formData.append("contract_address", trimmedContractAddress);
  
    const payload = {
      id: selectedItemId,
      formData: formData,
    };
  
    updateContractRequest(payload);
    handleCloseModal();
  };
  

  const handleOpenCreateModal = () => setShowCreateModal(true);
  const handleCloseCreateModal = () => {
    setSelectedCreateAbi(null);
    setSelectedCreateCode(null);
    setSelectedCreateByteCode(null);
    setCreateContractAddress('');
    setShowCreateModal(false);
  };

  const handleCreateAbiChange = (event) => setSelectedCreateAbi(event.target.files[0]);
  const handleCreateCodeChange = (event) => setSelectedCreateCode(event.target.files[0]);
  const handleCreateByteCodeChange = (event) => setSelectedCreateByteCode(event.target.files[0]);

  const handleCreateUpload = () => {
    const trimmedContractAddress = createContractAddress.trim();

    if (!isValidEthAddress(trimmedContractAddress)) {
      Swal.fire({
        icon: 'warning',
        iconColor: mainColors.warning,
        text: 'Please enter a valid contract address.',
        showCancelButton: true,
      });
      return;
    }

    if (!selectedCreateAbi || !selectedCreateCode || !selectedCreateByteCode) {
      Swal.fire({
        icon: 'warning',
        iconColor: mainColors.warning,
        text: 'Please select all files!',
        showCancelButton: true,
      });
      return;
    }
  
    const formData = new FormData();
    formData.append("abi", selectedCreateAbi);
    formData.append("code", selectedCreateCode);
    formData.append("byte_code", selectedCreateByteCode);
    formData.append("contract_address", trimmedContractAddress);
  
    createContractRequest(formData);
    handleCloseCreateModal();
  };
  
  const user = JSON.parse(localStorage.getItem('user'))?.user;
  return (
    <>
      <Table className="projects-table-design" responsive hover borderless>
        <thead>
          <tr className="text-t-body-color text-fs-tag">
            <th>#</th>
            <th>
              <MercurXSortableHeader
                label="Abi Name"
                isActive
                isAscending={contractsSortData.sortType === CONTRACTS_SORT_TYPES.ASCENDING}
                onClick={handleSort}
              />
            </th>
            <th>Run</th>
            <th>Detail</th>
            <th>Upload</th>
          </tr>
        </thead>
        {renderedItems?.length ? (
          <tbody>
            {renderedItems?.map((item, index) => (
              <tr key={item?.data?.id || `${item.name}-${index}`} className="text-t-head-color">
                <td>{rowOffset + index + 1}</td>
                <td>{item.name}</td>
                {/* <td>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleNavigate(item.name)}
                  >
                    Open
                  </button>
                </td> */}
                <td>
                  {user?.is_superuser && (
                    <div className="cursor-pointer mt-1"
                      onClick={() => handleNavigate(item.name, item)}>
                      <MercurXIcons
                        name="BsSkipForwardBtnFill"
                        size={24}
                        color={mainColors['primary']}
                      />
                    </div>
                  )}
                </td>
                <td>
                  {user?.is_superuser && (
                    <div className="cursor-pointer mt-1"
                      onClick={() => handleNavigateEdit(item.name, item)}>
                      <MercurXIcons
                        name="BsFillInfoSquareFill"
                        size={24}
                        color={mainColors['primary']}
                      />
                    </div>
                  )}
                </td>
                <td>
                  <div className="cursor-pointer mt-1"
                    onClick={() => handleOpenModal(item)}>
                    <MercurXIcons
                      name="BsUpload"
                      size={24}
                      color={mainColors['primary']}
                    />
                  </div>
                </td>

              </tr>
            ))}
          </tbody>
        ) : null}
      </Table>
      {!renderedItems?.length && (
        <div className="d-flex text-fs-body-md text-t-body-color justify-content-center">
          No abi found according to search results.
        </div>
      )}
      <MercurXTablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        visiblePages={visiblePages}
        isFirstPage={isFirstPage}
        isLastPage={isLastPage}
        onFirstPage={handleFirstPage}
        onPrevPage={handlePrevPage}
        onPageChange={handlePageChange}
        onNextPage={handleNextPage}
        onLastPage={handleLastPage}
      />
      <div className="text-end">
        <Button className='mt-2' onClick={handleOpenCreateModal}>
          Create Contract
        </Button>
      </div>
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Upload Files</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Contract Address</label>
            <input
              type="text"
              value={uploadContractAddress}
              onChange={(event) => setUploadContractAddress(event.target.value)}
              placeholder="0x..."
              className="form-control"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">ABI File</label>
            <input type="file" onChange={handleAbiChange} className="form-control" />
          </div>
          <div className="mb-3">
            <label className="form-label">Code File</label>
            <input type="file" onChange={handleCodeChange} className="form-control" />
          </div>
          <div className="mb-3">
            <label className="form-label">Byte Code File</label>
            <input type="file" onChange={handleByteCodeChange} className="form-control" />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseModal}>Close</Button>
          <Button variant="primary" onClick={handleUpload}>Upload</Button>
        </Modal.Footer>
      </Modal>

      {/* Create Modal */}
      <Modal show={showCreateModal} onHide={handleCloseCreateModal}>
        <Modal.Header closeButton>
          <Modal.Title>Create Contract</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Contract Address</label>
            <input
              type="text"
              value={createContractAddress}
              onChange={(event) => setCreateContractAddress(event.target.value)}
              placeholder="0x..."
              className="form-control"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">ABI File</label>
            <input type="file" onChange={handleCreateAbiChange} className="form-control" />
          </div>
          <div className="mb-3">
            <label className="form-label">Code File</label>
            <input type="file" onChange={handleCreateCodeChange} className="form-control" />
          </div>
          <div className="mb-3">
            <label className="form-label">Byte Code File</label>
            <input type="file" onChange={handleCreateByteCodeChange} className="form-control" />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseCreateModal}>Close</Button>
          <Button variant="primary" onClick={handleCreateUpload}>Upload</Button>
        </Modal.Footer>
      </Modal>

    </>
  );
}

const mapStateToProps = (state) => {
  return {
    abiHistory: state.abiReducer.abiHistory,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    abiHistoryRequest: (payload) => {
      dispatch(abiRequestAction(payload));
    },
    createContractRequest: (payload) => {
      dispatch(createContractActionRequest(payload));
    },
    updateContractRequest: (payload) => {
      dispatch(updateContractActionRequest(payload));
    },
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(MercurXContractTable);
